import { Flex, Image, Loading, Text } from '@byecode/ui'
import { type ApplicationSettingAuthentication, DevicePreviewEnum } from '@lighthouse/core'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAsyncRetry } from 'react-use'
import styled from 'styled-components'

import { ScanStatus } from '../../../types'
import LoginBox from '../LoginBox'
import * as CM from '../styles'
import type { AppLoginMode } from '../types'

const SCxRetry = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-white);
    text-align: center;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px; /* 133.333% */
    background-color: rgba(0, 0, 0, 0.6);
`
interface LoginProps {
    authentication: ApplicationSettingAuthentication
    language: string
    onWeChatLogin?: () => Promise<{ url: string; mark: string }> | undefined
    onChangeLanguage: (v: string) => void
    onCheckWechatLogin?: (mark: string) => Promise<ScanStatus>
    onChangeMode: (v: AppLoginMode) => void
}

export const WechatLogin: React.FunctionComponent<LoginProps> = ({
    authentication,
    language,
    onChangeLanguage,
    onWeChatLogin,
    onCheckWechatLogin,
    onChangeMode
}) => {
    const { login, register, name, logo, describe } = authentication

    const [scanStatus, setScanStatus] = useState<ScanStatus>()

    // const [timer, setTimer] = useState<NodeJS.Timeout | null>(null)
    const timerRef = useRef<NodeJS.Timeout | null>(null)

    const { t } = useTranslation()

    const handlePollWechatLogin = useCallback(
        async (mark: string) => {
            const res = await onCheckWechatLogin?.(mark)
            setScanStatus(res)
            if (res === ScanStatus.NOT_SCAN) {
                timerRef.current = setTimeout(() => handlePollWechatLogin(mark), 3000)
            }
        },
        [onCheckWechatLogin]
    )

    useEffect(() => {
        return () => {
            timerRef.current && clearTimeout(timerRef.current)
            timerRef.current = null
        }
    }, [])

    const {
        value: data,
        loading,
        retry
    } = useAsyncRetry(async () => {
        if (!onWeChatLogin) {
            return null
        }
        // 轮询检查非jsapi微信支付
        const res = await onWeChatLogin()
        res?.mark && handlePollWechatLogin(res?.mark)
        return res
    }, [])

    return (
        <LoginBox
            name={name}
            logo={logo}
            language={language}
            describe={describe}
            previewType={DevicePreviewEnum.DESKTOP}
            onChangeLanguage={onChangeLanguage}
            onBack={() => onChangeMode('home')}
        >
            <Flex direction="column" alignItems="center" gap={16}>
                {loading ? (
                    <Loading />
                ) : (
                    <div style={{ position: 'relative' }}>
                        {scanStatus === ScanStatus.QR_EXPIRED && <SCxRetry>{t('expiredPleaseRefresh')}</SCxRetry>}
                        <Image width={160} height={160} src={data?.url ?? ''} alt="微信二维码" />
                    </div>
                )}
                <Flex alignItems="center" gap={8}>
                    <CM.Icon type="WeChatLogo" color="var(--color-green-500)" />
                    <Text size={14} lineHeight="22px">
                        {t('scanWechatLogin')}
                    </Text>
                </Flex>
                <Flex
                    alignItems="center"
                    gap={6}
                    style={{
                        cursor: 'pointer'
                    }}
                    onClick={() => {
                        timerRef.current && clearTimeout(timerRef.current)
                        timerRef.current = null
                        retry()
                    }}
                >
                    <CM.Icon type="ArrowsClockwise" color="var(--color-gray-500)" />
                    <Text lineHeight="22px" size={14} color="var(--color-gray-500)">
                        {t('refresh')}
                    </Text>
                </Flex>
            </Flex>
        </LoginBox>
    )
}
